/**
 * "Ela tocou na notificação" — do push até o banco, sem autenticação nova.
 *
 * ── Por que o service worker não faz isso sozinho ─────────────────────────
 *
 * Ele não tem o JWT do Supabase. Dar um caminho próprio a ele — rota isenta,
 * token de serviço — seria superfície de segurança nova por causa de uma
 * métrica. Não precisa: o clique já abre o app, e o app já é autenticado.
 *
 * ── Por que `sessionStorage`, e por que ANTES de tudo ─────────────────────
 *
 * `AuthContext` faz `signInWithOAuth({ redirectTo: window.location.origin })`.
 * `origin` é só o domínio: **a query string não volta do Google.** Quem estava
 * deslogada e tocou na notificação perderia o `?aviso=` no meio do login, e o
 * clique dela viraria "ignorado".
 *
 * Por isso a captura roda no carregamento do módulo, antes de React montar e
 * muito antes de qualquer redirecionamento, e o valor espera no
 * `sessionStorage` até a sessão existir.
 *
 * ── Por que limpar a URL ──────────────────────────────────────────────────
 *
 * `?aviso=` na barra de endereço vira favorito, vira link compartilhado, e
 * volta a cada recarga. `replaceState` tira sem somar uma entrada no histórico
 * — com `pushState`, o botão "voltar" levaria de volta para a mesma URL e o
 * app tentaria registrar de novo.
 */

const CHAVE = 'fintracking:aviso-clicado';

/** O formato que o servidor aceita: `<tipo>.<referência>.<período>`. */
const FORMATO = /^[A-Za-z0-9_:-]+\.[A-Za-z0-9_:-]+\.[A-Za-z0-9_:-]+$/;

/**
 * Lê o parâmetro, guarda, e limpa a URL. Idempotente: chamar duas vezes não
 * duplica nada, porque a segunda não acha mais o parâmetro.
 *
 * Devolve o que guardou, ou `null` — o retorno existe para o teste, não para
 * quem chama no boot.
 */
export const capturarAviso = (): string | null => {
  try {
    const url = new URL(window.location.href);
    const aviso = url.searchParams.get('aviso');
    if (!aviso) return null;

    url.searchParams.delete('aviso');
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);

    // Formato inválido não vira pendência: guardar lixo faria o app tentar
    // registrar a cada carregamento até alguém limpar o navegador.
    if (!FORMATO.test(aviso)) return null;

    window.sessionStorage.setItem(CHAVE, aviso);
    return aviso;
  } catch {
    /*
     * `sessionStorage` lança em aba anônima com cookies bloqueados, e a URL
     * pode ser exótica. Perder o recibo é aceitável; quebrar o carregamento do
     * app por causa de uma métrica não é.
     */
    return null;
  }
};

/**
 * Manda o recibo que estiver esperando. Sem sessão, não faz nada e a pendência
 * continua guardada para a próxima tentativa.
 *
 * ── Por que limpa ANTES da resposta ───────────────────────────────────────
 *
 * Se limpasse depois, uma falha de rede deixaria a pendência viva e o app
 * tentaria de novo a cada montagem — e o servidor só aceita o primeiro clique,
 * então as tentativas seguintes não fariam nada além de barulho. Um recibo
 * perdido custa uma linha de métrica; um laço de repetição custa o canal.
 */
export const enviarReciboPendente = async (
  postar: (aviso: string) => Promise<unknown>,
): Promise<boolean> => {
  let aviso: string | null = null;
  try {
    aviso = window.sessionStorage.getItem(CHAVE);
    if (!aviso) return false;
    window.sessionStorage.removeItem(CHAVE);
  } catch {
    return false;
  }

  try {
    await postar(aviso);
    return true;
  } catch {
    // Silencioso de propósito: é uma métrica, e ela não pode virar um erro na
    // tela de quem só queria abrir o app.
    return false;
  }
};
